import React from 'react';
import SentimentAnalysisBadge from 'crono-fe-common/components/SentimentAnalysisBadge';
import {
  IScoreSettingsRulePatch,
  PatchScoringSettings,
} from 'crono-fe-common/types/DTO/patchScoringSettings';
import { SentimentAnalysisResult } from 'crono-fe-common/types/enums/sentimentAnalysisResult';
import { ExternalPropertyValueType } from 'crono-fe-common/types/externalProperty';

import { createHashedObject } from 'crono-fe-common/utils/object';
import {
  IScoreSettingsComparator,
  IScoreSettingsComparatorConfig,
  IScoreSettingsItemConfig,
} from './lead-score.types';
import { IncreaseXsIcon } from '@crono-one/design-system';
import { IScoreDetail } from 'crono-fe-common/types/leadScore';
import cn from 'clsx';

export const getTypeLabel = (type: PatchScoringSettings['type']) => {
  return {
    Account: 'Company',
    Prospect: 'Contact',
    Lead: 'Lead',
  }[type];
};

export const getScoreDescription = (type: PatchScoringSettings['type']) => {
  return {
    Account: 'Configure how company score is calculated across your companies.',
    Prospect: 'Configure how contact score is calculated across your contacts.',
    Lead: 'Configure how lead score is calculated across your leads.',
  }[type];
};

export const rulesConfigByType: Partial<
  Record<
    PatchScoringSettings['type'],
    {
      label: string;
      tooltip: string;
      emptyDescription: string;
    }
  >
> = {
  Account: {
    label: 'Company Data',
    tooltip: 'Company data is used to calculate the company score',
    emptyDescription: 'Use any fields in Crono to calculate your company score',
  },
  Prospect: {
    label: 'Contact Data',
    tooltip: 'Contact data is used to calculate the contact score',
    emptyDescription: 'Use any fields in Crono to calculate your contact score',
  },
  Lead: {
    label: 'Lead Data',
    tooltip: 'Lead data is used to calculate the lead score',
    emptyDescription: 'Use any fields in Crono to calculate your lead score',
  },
};

export const scoreSettingsValidationConfig: IScoreSettingsItemConfig<
  keyof PatchScoringSettings['validation']
>[] = [
  {
    enabledKey: 'hasValidEmail',
    weightKey: 'hasValidEmailWeight',
    scoreDetailKey: 'ValidEmail',
    getDetailLabel: () => 'Valid email',
    types: ['Prospect'],
    getLabel: (type) => `${getTypeLabel(type)} has a valid Email`,
    getTooltip: (type) =>
      `This will add to the score if the ${getTypeLabel(
        type,
      )} has a valid email`,
  },
  {
    enabledKey: 'hasValidPhone',
    weightKey: 'hasValidPhoneWeight',
    scoreDetailKey: 'ValidPhoneNumber',
    getDetailLabel: () => 'Valid phone number',
    getLabel: (type) => `${getTypeLabel(type)} has a valid Phone number`,
    getTooltip: (type) =>
      `This will add to the score if the ${getTypeLabel(
        type,
      )} has a valid phone number`,
  },
  {
    enabledKey: 'hasValidLinkedin',
    weightKey: 'hasValidLinkedinWeight',
    scoreDetailKey: 'ValidContactLinkedinProfileUrl',
    getDetailLabel: () => 'Valid LinkedIn page URL',
    getLabel: (type) =>
      type === 'Account'
        ? `${getTypeLabel(type)} has a valid LinkedIn Page URL`
        : `${getTypeLabel(type)} has a valid LinkedIn profile URL`,
    getTooltip: (type) =>
      type === 'Account'
        ? `This will add to the score if the ${getTypeLabel(
            type,
          )} has a valid LinkedIn Page URL`
        : `This will add to the score if the ${getTypeLabel(
            type,
          )} has a valid LinkedIn profile URL`,
  },
  {
    enabledKey: 'hasValidWebsiteUrl',
    weightKey: 'hasValidWebsiteUrlWeight',
    scoreDetailKey: 'ValidWebsiteUrl',
    getDetailLabel: () => 'Valid website URL',
    getLabel: (type) => `${getTypeLabel(type)} has a valid Website URL`,
    getTooltip: (type) =>
      `This will add to the score if the ${getTypeLabel(
        type,
      )} has a valid website URL`,
    types: ['Account'],
  },
];

export const getHashedSettingsValidationConfig = () => {
  return createHashedObject(scoreSettingsValidationConfig, 'scoreDetailKey');
};

export const scoreDetailsConfig: {
  scoreDetailKey: IScoreDetail['scoreDetailType'];
  getDetailLabel: (value: string | number | boolean) => string;
  labelSuffix?: JSX.Element;
}[] = [
  {
    scoreDetailKey: 'OptOut',
    getDetailLabel: () => 'Opted out',
  },
  {
    scoreDetailKey: 'NegativeReplies',
    getDetailLabel: () => 'Replied negatively',
    labelSuffix: (
      <SentimentAnalysisBadge
        sentimentAnalysisResult={SentimentAnalysisResult.NEGATIVE}
      />
    ),
  },
  {
    scoreDetailKey: 'PositiveReplies',
    getDetailLabel: () => 'Replied positively',
    labelSuffix: (
      <SentimentAnalysisBadge
        sentimentAnalysisResult={SentimentAnalysisResult.POSITIVE}
      />
    ),
  },
  {
    scoreDetailKey: 'Hiring',
    getDetailLabel: (value) => 'Company is hiring',
  },
];

export const getHashedScoreDetailsConfig = () => {
  return createHashedObject(scoreDetailsConfig, 'scoreDetailKey');
};

export const textComparators: IScoreSettingsComparator[] = [
  {
    label: 'Contains',
    value: 'Contains',
  },
  {
    label: 'Is',
    value: 'Is',
  },
  {
    label: 'known',
    alternateLabel: 'is known',
    value: 'StringValueKnown',
    shouldHaveValue: false,
  },
];

export const numericComparators: IScoreSettingsComparator[] = [
  {
    label: 'is higher than',
    alternateLabel: 'is greater than',
    value: 'BiggerThan',
  },
  {
    label: 'is lower than',
    alternateLabel: 'is less than',
    value: 'SmallerThan',
  },
  {
    label: 'is equal to',
    alternateLabel: 'equals',
    value: 'NumericEquals',
  },
  {
    label: 'known',
    alternateLabel: 'is known',
    value: 'NumericValueKnown',
    shouldHaveValue: false,
  },
  {
    label: 'between',
    alternateLabel: 'is between',
    value: 'Between',
  },
];

export const booleanComparators: IScoreSettingsComparator[] = [
  {
    label: 'Is true',
    value: 'IsTrue',
  },
  {
    label: 'Is false',
    value: 'IsFalse',
  },
];

export const timeComparators: IScoreSettingsComparator[] = [
  {
    label: 'Within',
    value: 'After',
  },
  {
    label: 'Older than',
    value: 'Before',
  },
];

export const getHashedComparatorsByValue = () => {
  return createHashedObject(
    [
      ...textComparators,
      ...numericComparators,
      ...booleanComparators,
      ...timeComparators,
    ],
    'value',
  );
};

export const dateUnits = [
  {
    label: 'Days',
    value: 'Days',
  },
  {
    label: 'Weeks',
    value: 'Weeks',
  },
  {
    label: 'Months',
    value: 'Months',
  },
  {
    label: 'Years',
    value: 'Years',
  },
];

export const getHashedDateUnits = () => {
  return createHashedObject(dateUnits, 'value');
};

export const comparatorsByValueType: Partial<
  Record<ExternalPropertyValueType, IScoreSettingsComparatorConfig>
> = {
  String: { comparators: textComparators, isSingle: true },
  Text: { comparators: textComparators, isSingle: true },
  Numeric: {
    comparators: numericComparators,
    shouldHaveUniqueComparators: true,
    isSingle: true,
  },
  Boolean: { comparators: booleanComparators, isSingle: true },
  List: { comparators: textComparators, isSingle: true },
  MultiSelect: { comparators: textComparators, isSingle: true },
  Date: {
    comparators: timeComparators,
    shouldHaveUniqueComparators: true,
    isSingle: true,
  },
  DateTime: {
    comparators: timeComparators,
    shouldHaveUniqueComparators: true,
    isSingle: true,
  },
};

const validationTypes: PatchScoringSettings['type'][] = [
  'Account',
  'Prospect',
  'Lead',
];

export const defaultValidationWeight = 5;
export const defaultSignalsWeight = 3;
export const defaultLookbackDays = 20;
export const defaultScoreSignals: PatchScoringSettings['signals'] = {
  hasEmailOpensSignalEnabled: true,
  emailOpensSignalWeight: 5,
  hasEmailClickSignalEnabled: true,
  emailClickSignalWeight: 5,
  hasWebsiteVisitsSignalEnabled: false,
  websiteVisitsSignalWeight: 5,
  hasNeutralRepliesSignalEnabled: true,
  neutralRepliesSignalWeight: 5,
  hasRoleChangeSignalEnabled: true,
  roleChangeSignalWeight: 5,
  hasCompanyChangeSignalEnabled: true,
  companyChangeSignalWeight: 5,
  hasHiringSignalEnabled: true,
  hiringSignalWeight: 5,
};

export const defaultScoringRules = {
  mediumScoreMin: 31,
  highScoreMin: 71,
};

const createDefaultScoreValidations = () =>
  scoreSettingsValidationConfig.reduce(
    (acc, config) => {
      const types =
        config.types && config.types.length ? config.types : validationTypes;

      types.forEach((type) => {
        acc[type] = {
          ...acc[type],
          [config.enabledKey]: true,
          [config.weightKey]: defaultValidationWeight,
        };
      });

      return acc;
    },
    validationTypes.reduce(
      (defaults, type) => {
        defaults[type] = {};
        return defaults;
      },
      {} as Record<
        PatchScoringSettings['type'],
        Partial<PatchScoringSettings['validation']>
      >,
    ),
  );

export const defaultScoreValidations = createDefaultScoreValidations();

export const disabledScoreSettingsDescriptions = {
  Account: [
    'Company scoring helps you qualify based on criteria that align with your Ideal Customer Profile. You can increase the score based on attributes like industry, size, tags and more.',
    'This ensures your team stays focused on the companies most likely to convert.',
  ],
  Prospect: [
    "Contact scoring allows you to qualify your contacts in Crono based criteria's that best fit your sales strategy. The higher the score, the more valuable the contact becomes for your sales pipeline. You can increase the score based on engagement, company information, and much more.",
    'This is a great way to keep track of your next sales moves.',
  ],
  Lead: [
    "Lead scoring allows you to qualify your contacts in Crono based criteria's that best fit your sales strategy. The higher the score, the more valuable the contact becomes for your sales pipeline. You can increase the score based on engagement, company information, and much more.",
    'This is a great way to keep track of your next sales moves.',
  ],
};

export const getScoreRange = (
  score: number | undefined | null,
  mediumScoreMin: number,
  highScoreMin: number,
) => {
  if (score === undefined || score === null) return null;

  if (score < mediumScoreMin) {
    return 'low';
  } else if (score < highScoreMin) {
    return 'medium';
  } else {
    return 'high';
  }
};

export const getColorsByScoreRange = (
  scoreRange: 'low' | 'medium' | 'high' | null | undefined,
) => {
  if (!scoreRange)
    return {
      text: 'text-gray-600',
      border: 'border-neutral-300',
    };

  return {
    low: {
      text: 'text-red-600',
      border: 'border-red-300',
      gradientBgToRight:
        'bg-[linear-gradient(90deg,_#FFEBEB_0%,_#FFFFFF_91.16%)]',
    },
    medium: {
      text: 'text-yellow-600',
      border: 'border-yellow-300',
      gradientBgToRight:
        'bg-[linear-gradient(90deg,_#FEF7E2_0%,_#FFFFFF_91.16%)]',
    },
    high: {
      text: 'text-green-600',
      border: 'border-green-300',
      gradientBgToRight:
        'bg-[linear-gradient(90deg,_#EEF9F6_0%,_#FFFFFF_91.16%)]',
    },
  }[scoreRange];
};

export const getScoreChange = (
  previousScore: number | undefined | null,
  currentScore: number | undefined | null,
) => {
  if (!previousScore || !currentScore) return null;

  if (currentScore > previousScore) {
    return 'grow';
  } else if (currentScore < previousScore) {
    return 'decrease';
  } else {
    return null;
  }
};

export const getIconByScoreChange = (
  scoreChange: 'grow' | 'decrease' | null | undefined,
  size: 'xs' | 'sm' | 'md' = 'md',
) => {
  if (!scoreChange) return null;

  const getSize = () => {
    return {
      xs: 'size-3',
      sm: 'size-4',
      md: 'size-6',
    }[size];
  };

  return {
    grow: <IncreaseXsIcon className={getSize()} />,
    decrease: <IncreaseXsIcon className={cn(getSize(), 'rotate-180')} />,
  }[scoreChange];
};

export const valueKeys: Exclude<keyof IScoreSettingsRulePatch, 'id'>[] = [
  'stringValues',
  'numericValue',
  'booleanValue',
  'timeUnit',
  'numericValueMin',
  'numericValueMax',
];

export const valueKeysByDataType: Record<
  ExternalPropertyValueType,
  (keyof IScoreSettingsRulePatch)[]
> = {
  String: ['stringValues'],
  Text: ['stringValues'],
  Numeric: ['numericValue', 'numericValueMin', 'numericValueMax'],
  Boolean: ['booleanValue'],
  List: ['stringValues'],
  MultiSelect: ['stringValues'],
  Date: ['numericValue', 'timeUnit'],
  DateTime: ['numericValue', 'timeUnit'],
};
